import { useMemo, useState } from 'react';
import type { ClashMarks, StandingOverlap } from './assemblyClash';
import type { CollisionRow } from './CollisionsTable';

// DOES A COVER GO INTO THE PART IT SITS ON? — Phase 6's covers, checked as the whole product's
// parts are (see ProductOverlaps), and shown in the same table.
//
// A cover sits ON wood: a pad on the face under a foot, a button in the mouth of the hole it caps.
// Either one inside the part is placed wrong — a pad sunk into a leg, a button deeper than its
// counterbore. Nothing is asked of a model here and nothing goes to the server: the covers are
// placed by the page (see covers, JointHoles), so the page measures them, on the very meshes the
// viewer holds (see AssemblyView.overlapsAmong). Every cover against every component part: two
// boxes apart cost nothing (see assemblyClash's boxGap).
//
// MEASURED WHERE THEY GO, never exploded: `key` is null while the covers stand clear of their
// places, and the rows are the last measurement's. By cover, so a cover switched off in the covers
// table — and so not in the scene — keeps the rows it had.

export type CoverUnit = { id: string; label: string };

export function useCoverCollisions({ covers, parts, key, of }: {
  // The covers in the scene, and the component parts, by the viewer's instance ids.
  covers: CoverUnit[];
  parts: CoverUnit[];
  // What the covers' places are, or null while they are not in them.
  key: string | null;
  // WHICH ANSWER THE COVERS ARE — a new one starts from nothing: rows kept for covers switched
  // off are rows of the answer they were measured in.
  of: unknown;
}) {
  const [byCover, setByCover] = useState<Map<string, StandingOverlap[]>>(new Map());
  const [done, setDone] = useState<string | null>(null);
  const [was, setWas] = useState(of);
  if (was !== of) {
    setWas(of);
    setByCover(new Map());
    setDone(null);
  }
  const names = useMemo(() => new Map([...covers, ...parts].map((u) => [u.id, u.label])), [covers, parts]);
  const [labels, setLabels] = useState<Map<string, string>>(new Map());
  const collide = useMemo(() => (key == null ? null : {
    key,
    pairs: covers.flatMap((c) => parts.map((p) => [c.id, p.id] as const)),
    onFound: (found: StandingOverlap[]) => {
      setByCover((was) => {
        const next = new Map(was);
        for (const c of covers) next.set(c.id, found.filter((o) => o.a === c.id));
        return next;
      });
      setLabels((was) => new Map([...was, ...names]));
      setDone(key);
    },
  }), [key, covers, parts, names]);

  const rows = useMemo<CollisionRow[]>(() => [...byCover.values()].flat().map((o) => ({
    key: `${o.a}|${o.b}`,
    a: labels.get(o.a) ?? o.a,
    b: labels.get(o.b) ?? o.b,
    depth: o.mm,
    mm: o.size,
    pickable: true,
  })), [byCover, labels]);

  // WHICH ROWS ARE PAINTED: every one until one is pressed — a check is looked at to be seen.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const pickedNow = useMemo(() => picked ?? new Set(rows.map((r) => r.key)), [picked, rows]);
  const onPick = (row: string) => {
    const next = new Set(pickedNow);
    if (!next.delete(row)) next.add(row);
    setPicked(next);
  };
  const onPickAll = (keys: string[]) => setPicked(new Set(keys));

  // THE PICKED ROWS' FACES, both sides of each, together — a part two covers go into wears both.
  const paint = useMemo<ClashMarks | null>(() => {
    const out: ClashMarks = {};
    for (const o of [...byCover.values()].flat()) {
      if (!pickedNow.has(`${o.a}|${o.b}`)) continue;
      for (const [id, faces] of Object.entries(o.marks)) {
        const had = out[id];
        out[id] = had ? Uint32Array.from([...had, ...faces]) : faces;
      }
    }
    return Object.keys(out).length ? out : null;
  }, [byCover, pickedNow]);

  // Not measured yet, or measured at other places than the ones now asked about.
  const checking = done == null || (key != null && key !== done);
  return { collide, paint, rows, picked: pickedNow, onPick, onPickAll, checking };
}
